import load from '#core/load';
import g from '#core/global';


const tooltip_$ = '.tooltip';
const trigger_$ = '.tooltip__trigger';
const content_$ = '.tooltip__content';
const hidden_c = 'tooltip__content--hidden';
const ready_attr = 'data-tooltip-ready';
const placement_attr = 'data-tooltip-placement';
const viewport_padding_x = 20;
const viewport_gap = 8;
const mobile_max = 596; // b3 — below this the tooltip centers via CSS
const hide_delay = 200;

let global_listeners_attached = false;


const module = {

	/**
	 * Init module
	 */
	init() {
		if (load.ran('tooltip')) return false;

		load.content(() => {
			init_all();
		});
	},

};

export default module;
module.init();


/**
 * Initialize all triggers in the document and attach global listeners.
 * Idempotent — safe to call multiple times (e.g. after dynamic DOM updates).
 */
export function init_all() {
	const $triggers = document.querySelectorAll(trigger_$);
	$triggers.forEach(init_trigger);
	init_global_listeners();
}


/**
 * Initialize a single tooltip trigger. The trigger's next sibling is expected
 * to be the tooltip content element. Idempotent.
 *
 * @param {HTMLElement} $trigger
 */
export function init_trigger($trigger) {
	if ($trigger.getAttribute(ready_attr) === g.true_s) return;

	const $content = $trigger.nextElementSibling;
	if (!$content || !$content.matches(content_$)) return;

	$trigger.setAttribute(ready_attr, g.true_s);

	let hide_timeout = null;

	const cancel_hide = () => {
		if (hide_timeout) {
			clearTimeout(hide_timeout);
			hide_timeout = null;
		}
	};

	const show_tooltip = () => {
		cancel_hide();
		$content.classList.remove(hidden_c);
		position_tooltip($trigger, $content);
	};

	const hide_tooltip = () => {
		cancel_hide();
		$content.classList.add(hidden_c);
	};

	const queue_hide = () => {
		cancel_hide();
		hide_timeout = setTimeout(hide_tooltip, hide_delay);
	};

	const toggle_tooltip = (e) => {
		e.preventDefault();
		e.stopPropagation();

		const is_hidden = $content.classList.contains(hidden_c);
		close_all_tooltips();
		if (is_hidden) show_tooltip();
		else hide_tooltip();
	};

	// Hover: show on enter, hide on leave (with delay so the user can move
	// from trigger to tooltip body without it disappearing immediately)
	$trigger.addEventListener(g.mouse_enter_e, show_tooltip);
	$trigger.addEventListener(g.mouse_leave_e, queue_hide);
	$content.addEventListener(g.mouse_enter_e, cancel_hide);
	$content.addEventListener(g.mouse_leave_e, queue_hide);

	// Keyboard: show on focus, hide on blur (no delay needed)
	$trigger.addEventListener(g.focus_e, show_tooltip);
	$trigger.addEventListener(g.blur_e, hide_tooltip);

	// Touch / non-anchor click: toggle
	$trigger.addEventListener(g.click_e, (e) => {
		// If the trigger is a link and the tooltip is already open, allow
		// the link to navigate normally
		if (
			$trigger.tagName === 'A'
			&& $trigger.hasAttribute('href')
			&& !$content.classList.contains(hidden_c)
		) {
			return;
		}
		toggle_tooltip(e);
	});

	// Keyboard activation for non-anchor (role=button) triggers
	if ($trigger.tagName !== 'A') {
		$trigger.addEventListener(g.key_down_e, (e) => {
			if (e.key === 'Enter' || e.key === ' ') {
				toggle_tooltip(e);
			}
		});
	}
}


/**
 * Attach document-level listeners for closing tooltips. Idempotent.
 */
export function init_global_listeners() {
	if (global_listeners_attached) return;
	global_listeners_attached = true;

	document.addEventListener(g.click_e, (e) => {
		if (!e.target.closest(tooltip_$)) close_all_tooltips();
	});
	document.addEventListener(g.key_down_e, (e) => {
		if (e.key === 'Escape') close_all_tooltips();
	});
}


/**
 * Position the tooltip with fixed coordinates derived from the trigger's
 * viewport rect, so the popover escapes any clipping scroll container. Opens
 * above the trigger by default (matching the design); `data-tooltip-placement`
 * can force `above`/`below`, and a tooltip that would overflow the top of the
 * viewport flips below automatically.
 * @param {HTMLElement} $trigger
 * @param {HTMLElement} $content
 */
function position_tooltip($trigger, $content) {
	// Below the mobile breakpoint the popover is centered via CSS — clear any
	// stale inline coordinates and let the stylesheet take over.
	if (window.innerWidth < mobile_max) {
		$content.style.removeProperty('top');
		$content.style.removeProperty('left');
		return;
	}

	const trigger_rect = $trigger.getBoundingClientRect();
	const tooltip_rect = $content.getBoundingClientRect();
	const viewport_width = window.innerWidth;

	const placement = $content.closest(tooltip_$)?.getAttribute(placement_attr);
	const space_above = trigger_rect.top;
	const show_below = placement === 'below'
		|| (placement !== 'above' && space_above < tooltip_rect.height + viewport_gap);

	const top = show_below
		? trigger_rect.bottom + viewport_gap
		: trigger_rect.top - tooltip_rect.height - viewport_gap;

	const trigger_center_x = trigger_rect.left + (trigger_rect.width / 2);
	const max_left = viewport_width - viewport_padding_x - tooltip_rect.width;
	const left = Math.max(
		viewport_padding_x,
		Math.min(trigger_center_x - (tooltip_rect.width / 2), max_left),
	);

	$content.style.top = `${top}px`;
	$content.style.left = `${left}px`;
}


/**
 * Close all open tooltips in the document
 */
function close_all_tooltips() {
	document.querySelectorAll(content_$).forEach(($tooltip) => {
		$tooltip.classList.add(hidden_c);
	});
}
